import { ref, computed } from 'vue'

export type NetworkStatus = 'online' | 'offline' | 'slow'

class NetworkService {
    private isOnline = ref(navigator.onLine)
    private connectionType = ref<string>('unknown')
    private effectiveType = ref<string>('unknown')
    private downlink = ref<number>(0)
    private rtt = ref<number>(0)
    private listeners: Array<(status: NetworkStatus) => void> = []

    constructor() {
        this.init()
    }

    private init() {
        // Listen for online/offline events
        window.addEventListener('online', this.handleOnline)
        window.addEventListener('offline', this.handleOffline)

        // Monitor connection quality if available
        if ('connection' in navigator) {
            const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection

            if (connection) {
                this.updateConnectionInfo(connection)
                connection.addEventListener('change', () => this.updateConnectionInfo(connection))
            }
        }

        // Initial check
        this.checkConnection()
    }

    private updateConnectionInfo(connection: any) {
        this.connectionType.value = connection.type || 'unknown'
        this.effectiveType.value = connection.effectiveType || 'unknown'
        this.downlink.value = connection.downlink || 0
        this.rtt.value = connection.rtt || 0

        console.log('[NetworkService] Connection info updated:', {
            type: this.connectionType.value,
            effectiveType: this.effectiveType.value,
            downlink: this.downlink.value,
            rtt: this.rtt.value
        })

        this.notifyListeners()
    }

    private handleOnline = () => {
        console.log('[NetworkService] Network is online')
        this.isOnline.value = true
        this.notifyListeners()
    }

    private handleOffline = () => {
        console.log('[NetworkService] Network is offline')
        this.isOnline.value = false
        this.notifyListeners()
    }

    private async checkConnection(): Promise<void> {
        // Additional check by trying to fetch a small resource
        try {
            const response = await fetch('/favicon.ico', {
                method: 'HEAD',
                cache: 'no-cache'
            })
            this.isOnline.value = response.ok
        } catch (error) {
            this.isOnline.value = false
        }
    }

    public getStatus(): NetworkStatus {
        if (!this.isOnline.value) {
            return 'offline'
        }

        // Consider connection slow if RTT > 500ms or downlink < 0.5 Mbps
        if (this.rtt.value > 500 || (this.downlink.value > 0 && this.downlink.value < 0.5)) {
            return 'slow'
        }

        return 'online'
    }

    public get online() {
        return computed(() => this.isOnline.value)
    }

    public get status() {
        return computed(() => this.getStatus())
    }

    public get connectionInfo() {
        return computed(() => ({
            type: this.connectionType.value,
            effectiveType: this.effectiveType.value,
            downlink: this.downlink.value,
            rtt: this.rtt.value
        }))
    }

    public onStatusChange(callback: (status: NetworkStatus) => void) {
        this.listeners.push(callback)
        // Call immediately with current status
        callback(this.getStatus())

        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(callback)
            if (index > -1) {
                this.listeners.splice(index, 1)
            }
        }
    }

    private notifyListeners() {
        const status = this.getStatus()
        this.listeners.forEach(listener => listener(status))
    }

    public async waitForOnline(timeout: number = 30000): Promise<boolean> {
        if (this.isOnline.value) return true

        return new Promise((resolve) => {
            const timeoutId = setTimeout(() => {
                cleanup()
                resolve(false)
            }, timeout)

            const unsubscribe = this.onStatusChange((status) => {
                if (status === 'online') {
                    cleanup()
                    resolve(true)
                }
            })

            const cleanup = () => {
                clearTimeout(timeoutId)
                unsubscribe()
            }
        })
    }

    public destroy() {
        window.removeEventListener('online', this.handleOnline)
        window.removeEventListener('offline', this.handleOffline)
        this.listeners = []
    }
}

export const networkService = new NetworkService()
